import FeatureGates from '@atlaskit/feature-gate-js-client';

import { QUERY_BRANCHES_PAGE_SIZE } from 'src/components/pipelines/constants';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { capturePipelinesExceptionWithTags } from 'src/components/pipelines/utils/sentry';
import { Branch } from 'src/components/types/src/refs';
import { StatsigFeatureKeys } from 'src/features';
import { authRequest } from 'src/utils/fetch';

import { getBranchesUrl } from '../redux/sagas/repository';

// eslint-disable-next-line import/no-default-export
export default function queryBranches(
  repositoryFullSlug: string,
  query: string,
  pagelen = QUERY_BRANCHES_PAGE_SIZE
): any {
  const afmDisableBranchPrefetching = FeatureGates.checkGate(
    StatsigFeatureKeys.afmDisableBranchPrefetching
  );

  if (afmDisableBranchPrefetching && !query) {
    return Promise.resolve({ values: [] });
  }

  const url = getBranchesUrl(repositoryFullSlug, {
    q: `name ~ "${query}"`,
    sort: '-target.date',
    pagelen,
  });
  return fetch(authRequest(url)).then(r => r.json());
}

export const createEmptyRef = (name: string, hash: string = ''): Branch => ({
  name,
  type: 'branch',
  target: {
    hash,
    author: {
      raw: '',
      type: 'author',
      user: {
        display_name: '',
        uuid: '',
        links: {
          self: { href: '' },
          html: { href: '' },
          avatar: { href: '' },
        },
        nickname: '',
        type: 'user',
        account_id: '',
      },
    },
    committer: {
      raw: '',
      type: 'committer',
      user: {
        display_name: '',
        uuid: '',
        links: {
          self: { href: '' },
          html: { href: '' },
          avatar: { href: '' },
        },
        nickname: '',
        type: 'user',
        account_id: '',
      },
    },
    date: new Date(),
    message: '',
    links: {
      self: { href: '' },
      comments: { href: '' },
      patch: { href: '' },
      html: { href: '' },
      diff: { href: '' },
      approve: { href: '' },
      request_changes: { href: '' },
      statuses: { href: '' },
    },
    type: 'commit',
    repository: {
      type: 'repository',
      full_name: '',
      name: '',
      uuid: '',
      links: {
        avatar: { href: '' },
        html: { href: '' },
        self: { href: '' },
        clone: [
          { href: '', name: 'https' },
          { href: '', name: 'ssh' },
        ],
      },
    },
    is_annotated: false,
  },
  links: {
    self: { href: '' },
    commits: { href: '' },
    html: { href: '' },
  },
});

export type PipelineRefsResponse = {
  values: Branch[];
  next: string | undefined;
};

export const fetchBranchesFromPipelines = async (
  url: string,
  onError?: (e: Error & { status?: number }) => void
): Promise<PipelineRefsResponse> => {
  const response = await fetch(authRequest(url));
  if (!response.ok) {
    const e = new Error(response.statusText || 'error loading branches');
    const errorWithStatus = { ...e, status: response.status };

    if (onError) {
      onError(errorWithStatus);
      return { values: [], next: undefined };
    } else {
      capturePipelinesExceptionWithTags(errorWithStatus, {
        segment: 'fetchBranchesFromPipelines',
        status: response.status.toString(),
      });
      throw errorWithStatus;
    }
  }

  const data = await response.json();

  // Transform the pipeline response into the expected ref format
  const seenBranches = new Set<string>();
  const preparedRefs = data.values
    .filter((pipeline: any) => {
      if (
        !pipeline.target?.ref_name ||
        seenBranches.has(pipeline.target.ref_name)
      ) {
        return false;
      }
      seenBranches.add(pipeline.target.ref_name);
      return true;
    })
    .map((pipeline: any) => {
      const ref = createEmptyRef(
        pipeline.target.ref_name,
        pipeline.target.commit?.hash || ''
      );

      // Add pipeline-specific links
      ref.links = {
        self: {
          href: pipeline.target.branch?.links?.self?.href || '',
        },
        commits: {
          href: pipeline.target.branch?.links?.commits?.href || '',
        },
        html: {
          href: pipeline.target.branch?.links?.html?.href || '',
        },
      };

      return ref;
    });

  return {
    values: preparedRefs,
    next: data.next,
  };
};

/**
 * Builds a URL for fetching pipelines with specific fields and filters
 * @param {string} repositoryFullSlug - Full repository slug (workspace/repo)
 * @param {Object} options - Options for building the URL
 * @param {number} options.page - Page number for pagination
 * @param {number} options.pagelen - Number of items per page
 * @param {string} options.currentUserUuid - Optional UUID of the current user for filtering
 * @returns {string} - Complete URL for fetching pipelines
 */
export const buildPipelineUrl = (
  repositoryFullSlug: string,
  {
    page = 1,
    pagelen = 50,
    currentUserUuid,
  }: { page?: number; pagelen?: number; currentUserUuid?: string | null }
): string => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pagelen: pagelen.toString(),
    sort: '-created_on',
    ...(currentUserUuid && { 'creator.uuid': currentUserUuid }),
  });

  // Add fields parameter to specify which fields we want
  const fields = [
    'values.target.ref_name',
    'values.target.commit',
    'values.target.branch',
    'values.created_on',
    'page',
    'next',
    'size',
  ].join(',');

  return `${envBaseUrl(
    '/!api/2.0'
  )}/repositories/${repositoryFullSlug}/pipelines/?${queryParams.toString()}&fields=${encodeURIComponent(
    fields
  )}`;
};
